import type { GameConfig } from '../../config/gameConfig';
import { getBuildingInputBatch } from '../../config/buildingConfig';
import { getHouseHeatPerHour, removeInventory, Settlement } from '../../engine/settlement';
import {
    addInventoryUntracked,
    addToolsUntracked,
    ensureHouses,
    removeInventoryUntracked,
} from '../../engine/settlement';
import { getBuildingStorage, getEffectiveInputCap } from '../../engine/settlement';
import type { SettlementBuildingKey } from '../../engine/settlement';
import { buildFoodTargets, moveHeatToHouse, moveToHouse } from './houses';
import type { AssistantMoveResult } from './types';
import {
    AssistantLogisticsState,
    DistanceClass,
    HouseMode,
    Location,
    NumericResourceKey,
    Payload,
    ResourceKey,
    Task,
} from '../../engine/assistant';

// TODO: fix all config keys to be unified for convenience
const BUILDING_CONFIG_KEY: Record<string, keyof GameConfig['buildings']> = {
    // SettlementBuildingKey -> GameConfig.buildings key
    lumberjack: 'lumberjackCamp',
    // most others match 1:1
};

function getBuildingConfigKey(buildingId: SettlementBuildingKey): keyof GameConfig['buildings'] {
    const key = BUILDING_CONFIG_KEY[String(buildingId)];
    return (key ||
        (buildingId as unknown as keyof GameConfig['buildings'])) as keyof GameConfig['buildings'];
}

const DISTANCE_ORDER: Record<DistanceClass, number> = { near: 1, city: 2, remote: 3 };

function maxDistance(a: DistanceClass, b: DistanceClass): DistanceClass {
    return DISTANCE_ORDER[a] >= DISTANCE_ORDER[b] ? a : b;
}

function getBuildingDistanceClass(config: GameConfig, location: Location): DistanceClass {
    const key = location.kind === 'building' ? location.buildingId : location.kind;
    const fromConfig = config.transport.assistants.distanceByBuilding[key];
    return fromConfig || 'near';
}

function distanceForLocation(config: GameConfig, loc: Location): DistanceClass {
    return getBuildingDistanceClass(config, loc);
}

function travelTicks(config: GameConfig, distance: DistanceClass, loaded: boolean): number {
    const base = config.transport.assistants.distanceTicks[distance];
    if (!loaded) return base;
    return Math.max(1, Math.ceil(base * (1 + config.transport.assistants.payloadPenaltyPercent)));
}

function refundPayloadToInventory(settlement: Settlement, payload: Payload | undefined): void {
    if (!payload) return;
    if (payload.kind === 'numeric') {
        addInventoryUntracked(settlement, payload.resource, payload.amount);
    } else if (payload.kind === 'weapons') {
        addInventoryUntracked(settlement, 'weapons', payload.amount);
    } else {
        // tool
        addToolsUntracked(settlement, [payload.tool as any]);
    }
}

function createOrResizeState(settlement: Settlement, count: number): AssistantLogisticsState {
    const existing = settlement.assistantLogistics;
    const next: AssistantLogisticsState =
        existing && existing.v === 1 ? existing : { v: 1, slots: [] };

    // grow
    while (next.slots.length < count) next.slots.push({ status: 'idle' });

    // shrink (refund payloads to avoid loss)
    while (next.slots.length > count) {
        const removed = next.slots.pop();
        if (removed && removed.status === 'travel') {
            // if the worker was delivering, payload is reserved; if picking up and already has payload, refund it.
            refundPayloadToInventory(settlement, removed.payload);
            // Also refund payload baked into task for deliver-* tasks.
            if (
                removed.task.kind === 'deliverHouse' ||
                removed.task.kind === 'deliverBuildingInput'
            ) {
                refundPayloadToInventory(settlement, removed.task.payload);
            }
        }
    }

    settlement.assistantLogistics = next;
    return next;
}

function inc(result: AssistantMoveResult, key: keyof AssistantMoveResult, by = 1): void {
    (result as any)[key] = ((result as any)[key] || 0) + by;
}

function tryWithdrawOneOutput(
    settlement: Settlement,
    buildingId: SettlementBuildingKey,
    resource: ResourceKey,
): Payload | null {
    const storage = getBuildingStorage(settlement, buildingId) as any;

    if (resource === 'tools') {
        if (!Array.isArray(storage.tools) || storage.tools.length <= 0) return null;
        const tool = storage.tools.shift();
        return tool ? { kind: 'tool', tool } : null;
    }

    if (resource === 'weapons') {
        const available = Number(storage.weapons || 0);
        if (available <= 0) return null;
        storage.weapons = available - 1;
        return { kind: 'weapons', amount: 1 };
    }

    const available = Number(storage[resource] || 0);
    if (available <= 0) return null;
    storage[resource] = available - 1;
    return { kind: 'numeric', resource: resource as NumericResourceKey, amount: 1 };
}

function tryDepositToBuildingInput(
    settlement: Settlement,
    config: GameConfig,
    buildingId: SettlementBuildingKey,
    resource: NumericResourceKey,
    amount: number,
): number {
    const storage = getBuildingStorage(settlement, buildingId) as any;
    const cap = getEffectiveInputCap(settlement, config, buildingId, resource);
    const current = Number(storage[resource] || 0);
    const space = Math.max(0, cap - current);
    const move = Math.min(space, amount);
    if (move <= 0) return 0;
    storage[resource] = current + move;
    return move;
}

// TODO: use this one instead of the inlined version later or
function depositPayload(
    settlement: Settlement,
    config: GameConfig,
    destination: Location,
    payload: Payload,
): { deposited: number; refunded: boolean } {
    if (destination.kind === 'inventory') {
        refundPayloadToInventory(settlement, payload);
        return { deposited: payload.kind === 'tool' ? 1 : payload.amount, refunded: false };
    }

    if (destination.kind === 'houses') {
        // We don't directly write to house storage; reuse existing balancing logic.
        if (payload.kind === 'tool') {
            // tools are not house-deliverable; refund.
            refundPayloadToInventory(settlement, payload);
            return { deposited: 0, refunded: true };
        }

        if (payload.kind === 'weapons') {
            // weapons are not house-deliverable; refund.
            refundPayloadToInventory(settlement, payload);
            return { deposited: 0, refunded: true };
        }

        // For houses we only ever deliver numeric resources.
        // We must call the correct helper based on mode.
        // The caller decides which helper; here we only handle raw deposit.
        // We'll deposit to inventory and re-run house logic to ensure
        // the house code is the single authority of allocation.
        // The caller already removed from inventory, so we temporarily re-add and then move.
        addInventoryUntracked(settlement, payload.resource, payload.amount);
        return { deposited: 0, refunded: false };
    }

    // destination.kind === 'building'
    if (payload.kind === 'tool') {
        // tools are only stored centrally; refund.
        refundPayloadToInventory(settlement, payload);
        return { deposited: 0, refunded: true };
    }

    if (payload.kind === 'weapons') {
        // weapons are only stored centrally; refund.
        refundPayloadToInventory(settlement, payload);
        return { deposited: 0, refunded: true };
    }

    const moved = tryDepositToBuildingInput(
        settlement,
        config,
        destination.buildingId,
        payload.resource,
        payload.amount,
    );
    if (moved <= 0) {
        refundPayloadToInventory(settlement, payload);
        return { deposited: 0, refunded: true };
    }
    return { deposited: moved, refunded: false };
}

function applyHouseDelivery(
    settlement: Settlement,
    config: GameConfig,
    task: Extract<Task, { kind: 'deliverHouse' }>,
    result: AssistantMoveResult,
): void {
    // We reserved payload at dispatch by removing it from settlement inventory.
    // Here we actually allocate it to houses.
    if (task.payload.kind !== 'numeric') {
        refundPayloadToInventory(settlement, task.payload);
        return;
    }

    if (task.mode === 'heat') {
        const heatTargets = buildFoodTargets(settlement, config);
        const moved = moveHeatToHouse(
            settlement,
            task.payload.resource as 'wood' | 'charcoal',
            1,
            heatTargets,
            config,
        );
        if (moved > 0) inc(result, task.metric, moved);
        return;
    }

    if (task.mode === 'food') {
        const foodTargets = buildFoodTargets(settlement, config);
        if (['food', 'wood', 'charcoal'].indexOf(task.payload.resource) !== -1) {
            const moved = moveToHouse(
                settlement,
                task.payload.resource as 'food' | 'charcoal' | 'wood',
                1,
                foodTargets,
            );
            if (moved > 0) inc(result, task.metric, moved);
        }

        return;
    }

    // mode === 'wood' (no explicit targets)
    const moved = moveToHouse(settlement, task.payload.resource as 'food' | 'charcoal' | 'wood', 1);
    if (moved > 0) inc(result, task.metric, moved);
}

function tickSlots(
    settlement: Settlement,
    config: GameConfig,
    state: AssistantLogisticsState,
    result: AssistantMoveResult,
): void {
    for (let i = 0; i < state.slots.length; i += 1) {
        const slot = state.slots[i];
        if (slot.status !== 'travel') continue;

        slot.ticksLeft -= 1;
        if (slot.ticksLeft > 0) continue;

        const task = slot.task;

        // Phase completion handler
        if (slot.phase === 'toPickup') {
            if (task.kind === 'pickupBuildingOutput') {
                const payload = tryWithdrawOneOutput(settlement, task.buildingId, task.resource);
                if (!payload) {
                    state.slots[i] = { status: 'idle' };
                    continue;
                }
                const source: Location = { kind: 'building', buildingId: task.buildingId };
                const destination: Location = { kind: 'inventory' };
                const dist = maxDistance(
                    distanceForLocation(config, source),
                    distanceForLocation(config, destination),
                );
                const ticks = travelTicks(config, dist, true);
                state.slots[i] = {
                    status: 'travel',
                    phase: 'toDropoff',
                    initialTicks: ticks,
                    ticksLeft: ticks,
                    task,
                    payload,
                };
                continue;
            }

            if (task.kind === 'transferBuildingToBuilding') {
                const payload = tryWithdrawOneOutput(
                    settlement,
                    task.sourceBuildingId,
                    task.resource,
                );
                if (!payload || payload.kind !== 'numeric') {
                    // if something went off (e.g., storage changed), abort.
                    if (payload) refundPayloadToInventory(settlement, payload);
                    state.slots[i] = { status: 'idle' };
                    continue;
                }
                const source: Location = { kind: 'building', buildingId: task.sourceBuildingId };
                const destination: Location = { kind: 'building', buildingId: task.destBuildingId };
                const dist = maxDistance(
                    distanceForLocation(config, source),
                    distanceForLocation(config, destination),
                );
                const ticks = travelTicks(config, dist, true);
                state.slots[i] = {
                    status: 'travel',
                    phase: 'toDropoff',
                    ticksLeft: ticks,
                    initialTicks: ticks,
                    task,
                    payload,
                };
                continue;
            }

            // deliver tasks never use toPickup
            state.slots[i] = { status: 'idle' };
            continue;
        }

        if (slot.phase === 'toDeliver') {
            // Arrived at destination with reserved payload.
            if (task.kind === 'deliverHouse') {
                applyHouseDelivery(settlement, config, task, result);
            } else if (task.kind === 'deliverBuildingInput') {
                if (task.payload.kind !== 'numeric') {
                    refundPayloadToInventory(settlement, task.payload);
                } else {
                    const moved = tryDepositToBuildingInput(
                        settlement,
                        config,
                        task.buildingId,
                        task.resource,
                        task.payload.amount,
                    );
                    if (moved > 0) inc(result, task.metric, moved);
                    else refundPayloadToInventory(settlement, task.payload);
                }
            } else {
                // shouldn't happen
                refundPayloadToInventory(settlement, slot.payload || undefined);
            }

            // Return empty (so this assistant is busy for the way back)
            const destination: Location =
                task.kind === 'deliverBuildingInput'
                    ? { kind: 'building', buildingId: task.buildingId }
                    : { kind: 'houses' };
            const dist = distanceForLocation(config, destination);
            const ticks = travelTicks(config, dist, false);
            state.slots[i] = {
                status: 'travel',
                phase: 'returnEmpty',
                ticksLeft: ticks,
                initialTicks: ticks,
                task,
            };
            continue;
        }

        if (slot.phase === 'toDropoff') {
            // Arrived at dropoff with payload.
            const payload = slot.payload;
            if (!payload) {
                state.slots[i] = { status: 'idle' };
                continue;
            }

            if (task.kind === 'pickupBuildingOutput') {
                // Always drops to inventory.
                refundPayloadToInventory(settlement, payload);
                inc(result, task.metric, 1);
                state.slots[i] = { status: 'idle' };
                continue;
            }

            if (task.kind === 'transferBuildingToBuilding') {
                if (payload.kind !== 'numeric') {
                    refundPayloadToInventory(settlement, payload);
                    state.slots[i] = { status: 'idle' };
                    continue;
                }
                const moved = tryDepositToBuildingInput(
                    settlement,
                    config,
                    task.destBuildingId,
                    task.resource,
                    payload.amount,
                );
                if (moved > 0) inc(result, task.metric, moved);
                else {
                    // If destination is full by the time we arrive, return wood to inventory.
                    refundPayloadToInventory(settlement, payload);
                }

                // return empty to inventory
                const dist = distanceForLocation(config, {
                    kind: 'building',
                    buildingId: task.destBuildingId,
                });
                const ticks = travelTicks(config, dist, false);

                state.slots[i] = {
                    status: 'travel',
                    phase: 'returnEmpty',
                    ticksLeft: ticks,
                    initialTicks: ticks,
                    task,
                };
                continue;
            }

            // deliver tasks should not use toDropoff
            refundPayloadToInventory(settlement, payload);
            state.slots[i] = { status: 'idle' };
            continue;
        }

        if (slot.phase === 'returnEmpty') {
            state.slots[i] = { status: 'idle' };
            continue;
        }
    }
}

function scheduleDeliverHouse(
    settlement: Settlement,
    config: GameConfig,
    slotIndex: number,
    state: AssistantLogisticsState,
    mode: HouseMode,
    resource: NumericResourceKey,
    metric: keyof AssistantMoveResult,
): boolean {
    const removed = removeInventoryUntracked(settlement, resource, 1);
    if (removed <= 0) return false;
    const payload: Payload = { kind: 'numeric', resource, amount: 1 };
    const dist = distanceForLocation(config, { kind: 'houses' });
    const ticks = travelTicks(config, dist, true);
    state.slots[slotIndex] = {
        status: 'travel',
        phase: 'toDeliver',
        ticksLeft: ticks,
        initialTicks: ticks,
        task: { kind: 'deliverHouse', mode, metric, payload },
        payload,
    };
    return true;
}

function scheduleDeliverBuildingInput(
    settlement: Settlement,
    config: GameConfig,
    slotIndex: number,
    state: AssistantLogisticsState,
    buildingId: SettlementBuildingKey,
    resource: NumericResourceKey,
    metric: keyof AssistantMoveResult,
): boolean {
    // quick feasibility check
    if ((settlement.buildings as any)?.[buildingId]?.qty <= 0) return false;
    const cap = getEffectiveInputCap(settlement, config, buildingId, resource);
    const storage = getBuildingStorage(settlement, buildingId) as any;
    const current = Number(storage[resource] || 0);
    if (current >= cap) return false;

    const removed = removeInventoryUntracked(settlement, resource, 1);
    if (removed <= 0) return false;

    const payload: Payload = { kind: 'numeric', resource, amount: 1 };
    const dist = distanceForLocation(config, { kind: 'building', buildingId });
    const ticks = travelTicks(config, dist, true);
    state.slots[slotIndex] = {
        status: 'travel',
        phase: 'toDeliver',
        initialTicks: ticks,
        ticksLeft: ticks,
        task: { kind: 'deliverBuildingInput', buildingId, resource, metric, payload },
        payload,
    };
    return true;
}

function schedulePickupBuildingOutput(
    settlement: Settlement,
    config: GameConfig,
    slotIndex: number,
    state: AssistantLogisticsState,
    buildingId: SettlementBuildingKey,
    resource: ResourceKey,
    metric: keyof AssistantMoveResult,
): boolean {
    // If there's nothing to pick up right now, don't schedule the trip.
    const storage = getBuildingStorage(settlement, buildingId) as any;

    const reserved = state.slots.filter(
        (s) =>
            s.status === 'travel' &&
            s.phase === 'toPickup' &&
            s.task.kind === 'pickupBuildingOutput' &&
            s.task.buildingId === 'blacksmith' &&
            s.task.resource === resource,
    ).length;

    let available: number =
        (resource === 'tools' ? storage.tools.length : storage.weapons) - reserved;

    if (!available || available <= 0) return false;

    const dist = distanceForLocation(config, { kind: 'building', buildingId });
    const ticks = travelTicks(config, dist, false);
    state.slots[slotIndex] = {
        status: 'travel',
        phase: 'toPickup',
        ticksLeft: ticks,
        initialTicks: ticks,
        task: { kind: 'pickupBuildingOutput', buildingId, resource, metric },
    };
    return true;
}

function scheduleTransferBuildingToBuilding(
    settlement: Settlement,
    config: GameConfig,
    slotIndex: number,
    state: AssistantLogisticsState,
    sourceBuildingId: SettlementBuildingKey,
    destBuildingId: SettlementBuildingKey,
    resource: NumericResourceKey,
    metric: keyof AssistantMoveResult,
): boolean {
    if ((settlement.buildings as any)?.[sourceBuildingId]?.qty <= 0) return false;
    if ((settlement.buildings as any)?.[destBuildingId]?.qty <= 0) return false;
    const sourceStorage = getBuildingStorage(settlement, sourceBuildingId) as any;
    if (Number(sourceStorage[resource] || 0) <= 0) return false;
    const cap = getEffectiveInputCap(settlement, config, destBuildingId, resource);
    const destStorage = getBuildingStorage(settlement, destBuildingId) as any;
    const current = Number(destStorage[resource] || 0);
    if (current >= cap) return false;

    const dist = distanceForLocation(config, {
        kind: 'building',
        buildingId: sourceBuildingId,
    });
    const ticks = travelTicks(config, dist, false);
    state.slots[slotIndex] = {
        status: 'travel',
        phase: 'toPickup',
        ticksLeft: ticks,
        initialTicks: ticks,
        task: {
            kind: 'transferBuildingToBuilding',
            sourceBuildingId,
            destBuildingId,
            resource,
            metric,
        },
    };
    return true;
}

function scheduleFoodOrHeatToHouse(
    settlement: Settlement,
    config: GameConfig,
    slotIndex: number,
    state: AssistantLogisticsState,
): boolean {
    let minPercentFood = 100;
    let minPercentHeat = 100;

    if (settlement.inventory.food > 0 && settlement.houses.length > 0) {
        const targets = buildFoodTargets(settlement, config);
        for (let i = 0; i < settlement.houses.length; i++) {
            const house = settlement.houses[i];
            const foodTarget = targets[i];

            const percentFood = house.storage.food / foodTarget;

            if (percentFood < minPercentFood) {
                minPercentFood = percentFood;
            }

            const heatTarget =
                getHouseHeatPerHour(house) * settlement.production.population.houseHeatTargetHours;

            const heatUnits =
                house.storage.wood * config.heat.warmth.wood +
                house.storage.charcoal * config.heat.warmth.charcoal;

            const percentHeat = heatUnits / heatTarget;

            if (percentHeat < minPercentHeat) {
                minPercentHeat = percentHeat;
            }
        }
    }

    const heat = function (): boolean {
        return (
            minPercentHeat < 100 &&
            (scheduleDeliverHouse(
                settlement,
                config,
                slotIndex,
                state,
                'heat',
                'charcoal',
                'movedHeatToHouses',
            ) ||
                scheduleDeliverHouse(
                    settlement,
                    config,
                    slotIndex,
                    state,
                    'heat',
                    'wood',
                    'movedHeatToHouses',
                ))
        );
    };

    const food = function (): boolean {
        return (
            minPercentFood < 100 &&
            scheduleDeliverHouse(
                settlement,
                config,
                slotIndex,
                state,
                'food',
                'food',
                'movedFoodToHouses',
            )
        );
    };

    const needsOrder = minPercentHeat < minPercentFood ? [heat, food] : [food, heat];

    for (let item of needsOrder) {
        if (item()) {
            return true;
        }
    }

    return false;
}

function planOneTaskForIdleSlot(
    settlement: Settlement,
    config: GameConfig,
    slotIndex: number,
    state: AssistantLogisticsState,
): void {
    // Priority order mirrors the old instant-move logic.

    // TODO: favor food/heat based on which has lower level so they won't freeze to death while they have food
    // TODO: provide +/- 2% more than they need of food/heat once threshold is met so they won't constantly work on that,
    //  they will need some state for that wheather they are working on that small bonus or not, but if not possible they will skip that

    // 1) Food to houses (targets)
    // 2) Heat to houses (targets) - prefer charcoal (higher density) if present
    if (scheduleFoodOrHeatToHouse(settlement, config, slotIndex, state)) {
        return;
    }

    // 3) Sawmill outputs -> inventory (planks)
    if (
        schedulePickupBuildingOutput(
            settlement,
            config,
            slotIndex,
            state,
            'sawmill',
            'planks',
            'movedPlanks',
        )
    ) {
        return;
    }

    // 4) Charcoal outputs -> inventory
    if (
        schedulePickupBuildingOutput(
            settlement,
            config,
            slotIndex,
            state,
            'charcoalPit',
            'charcoal',
            'movedCharcoal',
        )
    ) {
        return;
    }

    // 5) Blacksmith outputs -> inventory (tools/weapons)
    if (
        schedulePickupBuildingOutput(
            settlement,
            config,
            slotIndex,
            state,
            'blacksmith',
            'tools',
            'movedTools',
        )
    ) {
        return;
    }
    if (
        schedulePickupBuildingOutput(
            settlement,
            config,
            slotIndex,
            state,
            'blacksmith',
            'weapons',
            'movedWeapons',
        )
    ) {
        return;
    }

    // 6) Generic building outputs -> inventory
    const outputBuildings: SettlementBuildingKey[] = [
        'farm',
        'huntingHut',
        'fishingHut',
        'smokehouse',
        'weaver',
        'quarry',
        'masonry',
        'ironMine',
        'smelter',
        'goldMine',
        'mint',
    ];
    for (const buildingId of outputBuildings) {
        if ((settlement.buildings as any)?.[buildingId]?.qty <= 0) continue;
        const configKey = getBuildingConfigKey(buildingId);
        const out = (config.buildings as any)?.[configKey]?.output as
            | Record<string, any>
            | undefined;
        if (!out) continue;
        const storage = getBuildingStorage(settlement, buildingId) as any;
        for (const resource of Object.keys(out)) {
            const available = Number(storage[resource] || 0);
            if (available <= 0) continue;
            if (
                schedulePickupBuildingOutput(
                    settlement,
                    config,
                    slotIndex,
                    state,
                    buildingId,
                    resource as any,
                    'movedOutputs',
                )
            ) {
                return;
            }
        }
    }

    // 7) Lumberjack wood -> Sawmill input
    if (
        scheduleTransferBuildingToBuilding(
            settlement,
            config,
            slotIndex,
            state,
            'lumberjack',
            'sawmill',
            'wood',
            'movedToSawmill',
        )
    ) {
        return;
    }

    // 8) Inputs to blacksmith (ironIngots -> wood -> leather)
    if (settlement.buildings.blacksmith.qty > 0) {
        const storage = getBuildingStorage(settlement, 'blacksmith') as any;
        const smithCfg = config.production.blacksmith;

        const toolTotal =
            settlement.tools.farmers.length +
            settlement.tools.hunters.length +
            settlement.inventory.tools.length +
            storage.tools.length;
        const weaponTotal = settlement.inventory.weapons + storage.weapons;
        const missingTools = Math.max(0, settlement.production.blacksmith.targetTools - toolTotal);
        const missingWeapons = Math.max(
            0,
            settlement.production.blacksmith.targetWeapons - weaponTotal,
        );
        const toolBatches = Math.ceil(missingTools / smithCfg.toolsPerBatch);
        const weaponBatches = Math.ceil(missingWeapons / smithCfg.weaponsPerBatch);
        const ingotsNeeded =
            toolBatches * smithCfg.toolIngots + weaponBatches * smithCfg.weaponIngots;
        const woodNeeded = toolBatches * smithCfg.toolWood + weaponBatches * smithCfg.weaponWood;
        const leatherNeeded = weaponBatches * smithCfg.weaponLeather;

        const missingIngots = Math.max(0, ingotsNeeded - storage.ironIngots);
        const missingWood = Math.max(0, woodNeeded - storage.wood);
        const missingLeather = Math.max(0, leatherNeeded - storage.leather);

        if (missingIngots > 0 && settlement.inventory.ironIngots > 0) {
            if (
                scheduleDeliverBuildingInput(
                    settlement,
                    config,
                    slotIndex,
                    state,
                    'blacksmith',
                    'ironIngots',
                    'movedInputs',
                )
            ) {
                return;
            }
        }
        if (missingWood > 0 && settlement.inventory.wood > 0) {
            if (
                scheduleDeliverBuildingInput(
                    settlement,
                    config,
                    slotIndex,
                    state,
                    'blacksmith',
                    'wood',
                    'movedInputs',
                )
            ) {
                return;
            }
        }
        if (missingLeather > 0 && settlement.inventory.leather > 0) {
            if (
                scheduleDeliverBuildingInput(
                    settlement,
                    config,
                    slotIndex,
                    state,
                    'blacksmith',
                    'leather',
                    'movedInputs',
                )
            ) {
                return;
            }
        }
    }

    // 9) Inputs to other buildings that require it (gated by worker count)
    const inputBuildings: SettlementBuildingKey[] = [
        'smokehouse',
        'weaver',
        'quarry',
        'masonry',
        'ironMine',
        'smelter',
        'mint',
    ];
    for (const buildingId of inputBuildings) {
        const jobCount = Number((settlement.jobs as any)?.[buildingId] || 0);
        if (jobCount <= 0 || (settlement.buildings as any)?.[buildingId]?.qty <= 0) continue;
        const configKey = getBuildingConfigKey(buildingId);
        const input = (config.buildings as any)?.[configKey]?.input as
            | Record<string, any>
            | undefined;
        if (!input) continue;

        for (const resource of Object.keys(input)) {
            const r = resource as NumericResourceKey;
            if (Number((settlement.inventory as any)[r] || 0) <= 0) continue;
            if (
                scheduleDeliverBuildingInput(
                    settlement,
                    config,
                    slotIndex,
                    state,
                    buildingId,
                    r,
                    'movedInputs',
                )
            ) {
                return;
            }
        }
    }

    // 10) Wood to charcoal pit (inputs)
    if (settlement.buildings.charcoalPit.qty > 0 && settlement.jobs.burners > 0) {
        const storage = getBuildingStorage(settlement, 'charcoalPit') as any;
        const perBatch = getBuildingInputBatch(config, 'charcoalPit', 'wood') ?? 0;
        const desired = Math.ceil(settlement.jobs.burners * perBatch);
        const cap = getEffectiveInputCap(settlement, config, 'charcoalPit', 'wood');
        const current = Number(storage.wood || 0);
        if (settlement.inventory.wood > 0 && current < Math.min(cap, desired)) {
            if (
                scheduleDeliverBuildingInput(
                    settlement,
                    config,
                    slotIndex,
                    state,
                    'charcoalPit',
                    'wood',
                    'movedToCharcoal',
                )
            ) {
                return;
            }
        }
    }

    // 11) Lumberjack output (wood) -> inventory
    if (
        schedulePickupBuildingOutput(
            settlement,
            config,
            slotIndex,
            state,
            'lumberjack',
            'wood',
            'movedToStorage',
        )
    ) {
        return;
    }
}

export function runAssistantMoves(settlement: Settlement, config: GameConfig): AssistantMoveResult {
    ensureHouses(settlement);

    const assistants = Math.max(0, settlement.jobs.assistants || 0);

    // TODO: state check should happen just once for each save
    const state = createOrResizeState(settlement, assistants);

    const result: AssistantMoveResult = {
        movedFoodToHouses: 0,
        movedHeatToHouses: 0,
        movedCharcoalToHouses: 0,
        movedToBlacksmith: 0,
        movedWoodToHouses: 0,
        movedPlanks: 0,
        movedCharcoal: 0,
        movedTools: 0,
        movedWeapons: 0,
        movedOutputs: 0,
        movedInputs: 0,
        movedToSawmill: 0,
        movedToCharcoal: 0,
        movedToStorage: 0,
        movedAny: false,
    };

    // 1) Progress in-flight trips; deliveries/pickups complete here.
    tickSlots(settlement, config, state, result);

    // 2) Assign new trips to idle assistants.
    for (let i = 0; i < state.slots.length; i += 1) {
        if (state.slots[i].status !== 'idle') continue;
        planOneTaskForIdleSlot(settlement, config, i, state);
    }

    // derived
    result.movedAny =
        result.movedFoodToHouses > 0 ||
        result.movedCharcoalToHouses > 0 ||
        result.movedWoodToHouses > 0 ||
        result.movedPlanks > 0 ||
        result.movedCharcoal > 0 ||
        result.movedTools > 0 ||
        result.movedWeapons > 0 ||
        result.movedOutputs > 0 ||
        result.movedInputs > 0 ||
        result.movedToSawmill > 0 ||
        result.movedToCharcoal > 0 ||
        result.movedToStorage > 0;

    return result;
}
